import React from 'react';

// GOV13 base-plan polish — presentation-only base-plan SUBSCRIPTION/payment state, rendered SEPARATELY from plan
// identity (plan identity comes from canonical entitlements via BillingCurrentPlanCard). A valid canonical plan is
// NEVER rendered inactive just because there is no Stripe subscription; there is NO grey "Activate base plan"
// button for a tenant that already has a valid plan; the Stripe portal action appears ONLY when the tenant is
// canonically portal-eligible AND the caller may manage billing (admin/administrator). No fetch, no inference.
const TONE = { green: 'bg-green-100 text-green-800', gray: 'bg-gray-200 text-gray-700', amber: 'bg-amber-100 text-amber-800',
  blue: 'bg-blue-100 text-blue-800', red: 'bg-red-100 text-red-700', purple: 'bg-purple-100 text-purple-800' };

export function subscriptionState(billingState, hasValidPlan) {
  const b = billingState || {};
  const s = String(b.billing_provider_status || '').toLowerCase();
  const c = String(b.cancellation_state || '').toLowerCase();
  if (s === 'active_paid') return { key: 'ACTIVE_STRIPE_SUBSCRIPTION', label: 'Active paid base-plan subscription', tone: 'green' };
  if (b.manual_contract || b.grandfathered) return { key: 'GRANDFATHERED_OR_EXTERNALLY_MANAGED', label: 'Grandfathered / externally managed', tone: 'purple' };
  if (s === 'past_due') return { key: 'PAST_DUE', label: 'Past due', tone: 'red' };
  if (s === 'payment_action_required' || b.payment_action_required) return { key: 'PAYMENT_ACTION_REQUIRED', label: 'Payment action required', tone: 'amber' };
  if (c === 'cancel_at_period_end' || b.cancel_at_period_end) return { key: 'CANCEL_AT_PERIOD_END', label: 'Cancels at period end', tone: 'amber' };
  if (c === 'cancelled') return { key: 'CANCELLED', label: 'Cancelled', tone: 'gray' };
  if (s === 'incomplete') return { key: 'INCOMPLETE', label: 'Incomplete', tone: 'amber' };
  if (hasValidPlan) return { key: 'NO_ACTIVE_STRIPE_SUBSCRIPTION', label: 'No active Stripe base-plan subscription', tone: 'gray' };
  return { key: 'UNKNOWN_WITH_REASON', label: 'Subscription state unavailable', tone: 'gray' };
}

export default function BasePlanSubscriptionState({ plan, billingState, portalEligible = false, canManage = false,
                                                   onOpenPortal, portalPending = false }) {
  const hasValidPlan = !!(plan && plan.display_name);
  const st = subscriptionState(billingState, hasValidPlan);
  const showPortal = portalEligible && canManage;

  return (
    <div data-testid="base-plan-subscription-state">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600">Base-plan subscription:</span>
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${TONE[st.tone]}`}
          data-testid="subscription-state-badge" data-state={st.key}>{st.label}</span>
      </div>

      <p className="text-xs text-gray-500 mt-2" data-testid="subscription-state-message">
        {st.key === 'ACTIVE_STRIPE_SUBSCRIPTION'
          ? (billingState?.next_billing_date ? `Your paid subscription is active. Next billing: ${billingState.next_billing_date}` : 'Your paid subscription is active.')
          : hasValidPlan
            ? 'Your current plan is active in Servisfy. No active Stripe base-plan subscription is attached — plan changes are handled under “Change plan” above.'
            : 'Subscription state is unavailable right now. This does not change your plan.'}
      </p>

      {/* Stripe portal action ONLY when canonically eligible AND the caller may manage billing. There is NO grey
          "Activate base plan" button for a tenant that already has a valid plan. */}
      {showPortal ? (
        <button type="button" onClick={onOpenPortal} disabled={portalPending}
          data-testid="open-stripe-portal-btn"
          className="mt-3 px-3 py-1.5 text-sm rounded bg-gradient-to-r from-indigo-500 to-blue-600 text-white disabled:opacity-60">
          {portalPending ? 'Opening secure portal…' : 'Manage billing'}
        </button>
      ) : (
        <p className="text-[11px] text-gray-400 mt-3" data-testid="portal-unavailable-note">
          {canManage
            ? 'A Stripe billing portal will be available once a paid base-plan subscription or payment method exists.'
            : 'Only a Business Administrator can manage base-plan billing.'}
        </p>
      )}
    </div>
  );
}
